import { useCallback, useState } from 'react';
import useStore, { storeState } from '~/store';
import { Product } from '~/store/slices/quote/types';
import { isArideParkFlow } from '~/utils/promotionUtils';
import { reportError } from '~/utils/reportError';

/**
 * Hook que centraliza a lógica de seleção de produtos e atualização via PUT /Quotes.
 * Callbacks estáveis (useCallback) para evitar re-renders em cascata nos cards.
 */
export function usePersonalCoverageProducts(orgParam: string | null) {
  const [productsLoading, setProductsLoading] = useState(false);

  const quoteHash = useStore((s) => s.quoteResponse?.quote_hash);
  const quoteAsyncUpdate = useStore((s) => s.quoteAsyncUpdate);
  const setProducts = useStore((s) => s.setProducts);
  const setTotalValue = useStore((s) => s.setTotalValue);
  const setServiceCharge = useStore((s) => s.setServiceCharge);
  const products = useStore((s) => s.products);
  const promotionalMonths = useStore((s) => s.quoteResponse?.promotional_months);

  const fetchProductsData = useCallback(
    async (nextProducts: Product[], onError?: () => void) => {
      if (!quoteHash) return;

      setProductsLoading(true);
      try {
        const { data } = await quoteAsyncUpdate(quoteHash, nextProducts, orgParam ?? undefined);
        setServiceCharge(data.financial_charges);
        setTotalValue(data.total);
      } catch (error) {
        if (onError) onError();
        if (error instanceof Error) console.error(error.message);
        reportError('Erro ao atualizar produtos da cotação', error, {
          context: 'PersonalCoverage_updateProducts',
          quoteHash,
          productsCount: nextProducts?.length
        });
        storeState.addToast({
          title: 'Erro ao tentar atualizar os produtos.',
          message: 'Não foi possível atualizar os produtos no momento. Por favor, tente novamente mais tarde.',
          type: 'error'
        });
      } finally {
        setProductsLoading(false);
      }
    },
    [quoteHash, quoteAsyncUpdate, setServiceCharge, setTotalValue, orgParam]
  );

  const handleProduct = useCallback(
    (product: Product) => {
      const oldProducts = [...products];
      const isRemoving = products.some((p) => p.product_id === product.product_id);
      const newProducts = isRemoving
        ? products.filter((p) => p.product_id !== product.product_id)
        : [...products, product];

      setProducts(newProducts);

      if (isArideParkFlow(newProducts)) {
        useStore.setState((state) => ({
          promotionData: {
            ...state.promotionData,
            has_promotion: true,
            is_promotion_available: true
          }
        }));
      } else if (!(promotionalMonths && promotionalMonths > 0)) {
        useStore.setState((state) => ({
          promotionData: {
            ...state.promotionData,
            has_promotion: false,
            is_promotion_available: false
          }
        }));
      }

      if (newProducts.length === 0) {
        setTotalValue(0);
        setServiceCharge(0);
      } else {
        fetchProductsData(newProducts, () => setProducts(oldProducts));
      }
    },
    [products, setProducts, setTotalValue, setServiceCharge, promotionalMonths, fetchProductsData]
  );

  return { products, productsLoading, handleProduct, fetchProductsData };
}
